/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { Camera, Video, Hash, Upload, Car, Plane, Shield, Wrench, ClipboardList, HelpCircle } from 'lucide-react';
import { toLocal } from '../utils/dateTime';
import logoBlack from '../assets/truvideo-logo-black.png';
import logoWhite from '../assets/truvideo-logo-white.png';
import GoodLightingIcon from '../assets/Good lighting.svg';
import GoodLightingWhiteIcon from '../assets/Good lighting-white.svg';
import RecordMediaIcon from '../assets/Record media.svg';
import RecordMediaWhiteIcon from '../assets/Record media-white.svg';
import ReviewSubmitIcon from '../assets/Review & submit.svg';
import ReviewSubmitWhiteIcon from '../assets/Review & submit-white.svg';
import SecureTransmissionIcon from '../assets/Secure transmission.svg';
import SecureTransmissionWhiteIcon from '../assets/Secure transmission-white.svg';
import cameraIcon from '../assets/camera.svg';
import cameraWhiteIcon from '../assets/camera-white.svg';
import videoIcon from '../assets/video.svg';
import videoWhiteIcon from '../assets/video-white.svg';
import scannerIcon from '../assets/scanner.svg';
import scannerWhiteIcon from '../assets/scanner-white.svg';
import filesIconAsset from '../assets/files.svg';
import filesWhiteIcon from '../assets/files-white.svg';
import audioIcon from '../assets/audio.svg';
import audioWhiteIcon from '../assets/audio-white.svg';
import { DEFAULT_CUSTOMER_REFERENCE_LABEL, resolveCustomerReferenceLabel } from '../utils/customerReferenceLabel';
import { EXT_CATEGORY_MAP } from '../constants/mimeMap';
import aviationImage from '../assets/aviation.png';
import automotiveImage from '../assets/automotive.png';
import fieldServiceImage from '../assets/field-services.png';
import generalImage from '../assets/general.png';
import insuranceImage from '../assets/insurance.png';

export const industryConfig = {
  'automotive': { icon: <Car size={26} strokeWidth={1.75} />, gradient: 'from-blue-600 to-indigo-600', light: 'bg-blue-50', bg: 'bg-blue-600', text: 'text-blue-700' },
  'aviation': { icon: <Plane size={26} strokeWidth={1.75} />, gradient: 'from-sky-500 to-blue-600', light: 'bg-sky-50', bg: 'bg-sky-600', text: 'text-sky-700' },
  'insurance': { icon: <Shield size={26} strokeWidth={1.75} />, gradient: 'from-slate-700 to-slate-900', light: 'bg-slate-50', bg: 'bg-slate-800', text: 'text-slate-900' },
  'field_services': { icon: <Wrench size={26} strokeWidth={1.75} />, gradient: 'from-amber-500 to-orange-600', light: 'bg-amber-50', bg: 'bg-amber-600', text: 'text-amber-700' },
  'general': { icon: <ClipboardList size={26} strokeWidth={1.75} />, gradient: 'from-gray-600 to-gray-800', light: 'bg-gray-50', bg: 'bg-gray-700', text: 'text-gray-700' }
};

export const typeConfig = {
  photo: { Icon: Camera, label: 'Photo', bg: 'bg-emerald-600', light: 'bg-emerald-50', text: 'text-emerald-700' },
  video: { Icon: Video, label: 'Video', bg: 'bg-sky-600', light: 'bg-sky-50', text: 'text-sky-700' },
  identifier: { Icon: Hash, label: 'Scan', bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-700' },
  file: { Icon: Upload, label: 'File', bg: 'bg-violet-600', light: 'bg-violet-50', text: 'text-violet-700' },
};

// Named constants 
export const DEFAULT_TOTAL_STEPS = 5;

// No hardcoded colors here anymore, using DaisyUI themes
export const DAISY_THEMES = []; // Deprecated, but keeping empty to avoid breakages if ref'd

// Shared UI literals (Sonar: S1192)
const CSS_POSITION_ABSOLUTE = 'absolute';

const PhonePreview = ({ workflowName = "", name, submitterName, assigneeName = "", customerReferenceNumber = "", customerReferenceLabel, systemReferenceNumber = "", industry, description, items, previewStep, branding, expiresAt, workflowIndustryLogoUrls, globalInstructionUrl }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTriggerRef = useRef(null);
  const phoneScreenRef = useRef(null);
  const instructionRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);

  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [showTooltipInstr, setShowTooltipInstr] = useState(false);
  const tooltipTriggerInstrRef = useRef(null);
  const [tooltipPosInstr, setTooltipPosInstr] = useState({ top: 0, left: 0 });
  const [showTooltipStep, setShowTooltipStep] = useState(false);
  const tooltipTriggerStepRef = useRef(null);
  const [tooltipPosStep, setTooltipPosStep] = useState({ top: 0, left: 0 });
  const [showTooltipDone, setShowTooltipDone] = useState(false);
  const tooltipTriggerDoneRef = useRef(null);
  const [tooltipPosDone, setTooltipPosDone] = useState({ top: 0, left: 0 });

  const [showNameTooltipStep, setShowNameTooltipStep] = useState(false);
  const nameTooltipTriggerStepRef = useRef(null);
  const [nameTooltipPosStep, setNameTooltipPosStep] = useState({ top: 0, left: 0 });

  const [showNameTooltipDone, setShowNameTooltipDone] = useState(false);
  const nameTooltipTriggerDoneRef = useRef(null);
  const [nameTooltipPosDone, setNameTooltipPosDone] = useState({ top: 0, left: 0 });

  const checkTruncation = () => {
    if (instructionRef.current) {
      const wasExpanded = !instructionRef.current.classList.contains('truncate');
      if (wasExpanded) {
        instructionRef.current.classList.add('truncate');
      }

      const isTruncated = instructionRef.current.scrollWidth > instructionRef.current.clientWidth;

      if (wasExpanded) {
        instructionRef.current.classList.remove('truncate');
      }

      setCanExpand(isTruncated);
    }
  };

  useEffect(() => {
    setIsExpanded(false);
    // Give browser time to layout before checking truncation
    const timer = setTimeout(checkTruncation, 100);
    return () => clearTimeout(timer);
  }, [previewStep, items]);

  useEffect(() => {
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, []);


  const screens = ['welcome', 'instructions', ...items.map((_, i) => `step-${i}`), 'done'];
  const current = screens[Math.min(previewStep, screens.length - 1)] || 'welcome';

  const renderScreen = () => {
    const currentTheme = branding?.theme || 'light';

    // Check if the current theme is one of DaisyUI's dark themes
    const isDark = ['dark', 'synthwave', 'halloween', 'aqua', 'forest', 'black', 'luxury', 'dracula', 'business', 'night', 'coffee', 'dim', 'sunset'].includes(currentTheme);

    const logo = branding?.logoUrl || (isDark ? logoWhite : logoBlack);

    // Get the expiry date
    const now = new Date();
    const date = expiresAt ?? now;

    // Format the expiry date
    const formatted = toLocal(date, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).replace(' at ', ', ');
    const dynamicReferenceLabel = resolveCustomerReferenceLabel({ customerReferenceLabel });

    const getImage = (industry) => {
      if (!workflowIndustryLogoUrls) return automotiveImage;
      switch (industry) {
        case 'automotive':
          return workflowIndustryLogoUrls.automotive ? workflowIndustryLogoUrls.automotive : automotiveImage
        case 'aviation':
          return workflowIndustryLogoUrls.aviation ? workflowIndustryLogoUrls.aviation : aviationImage
        case 'insurance':
          return workflowIndustryLogoUrls.insurance ? workflowIndustryLogoUrls.insurance : insuranceImage
        case 'field_services':
          return workflowIndustryLogoUrls.fieldServices ? workflowIndustryLogoUrls.fieldServices : fieldServiceImage
        case 'general':
          return workflowIndustryLogoUrls.general ? workflowIndustryLogoUrls.general : generalImage
        default:
          return automotiveImage
          break;
      }
    };

    if (current === 'welcome') {
      const welcomeTitle = name || 'Welcome, {name}';

      const rawWelcomeSubtitle = branding?.welcomeSubtitle || `Creating a claim can be stressful but we’re going to walk you through every step to help you get things moving along quickly and efficiently. ${assigneeName ? `- ${assigneeName}` : '- Creator Name'}`
      const welcomeSubTitle = rawWelcomeSubtitle?.replaceAll('{creator_name}', assigneeName || '{creator_name}');

      return (
        <div className="flex-1 flex flex-col font-sans bg-base-100 text-base-content min-h-full">
          {/* Header Row */}
          <div className="flex justify-between items-center px-3 pt-3 mb-3 gap-2">
            <img src={logo} alt="TruVideo" className="w-auto max-w-[70px] h-[30px] max-h-[30px] object-contain" />
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-bold text-base-content">Expires:</span>
              {!isNaN(date.getTime()) ? <div className="bg-success/10 border border-success/20 rounded-full px-1 py-0.5 text-success font-bold text-[9px] tabular-nums">
                {formatted}
              </div> : ""}
            </div>
          </div>

          {/* Welcome Text */}
          <div className="px-3 mb-3">
            <h1 className="text-[14px] font-black tracking-tight leading-tight mb-1.5 text-base-content custom-line-break">
              {welcomeTitle}
            </h1>
            <div className="flex flex-wrap flex-col gap-0.5 text-[10px] text-base-content">
              <div className="flex flex-wrap justify-between items-start w-full custom-line-break">
                <span className='custom-line-break'>{dynamicReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicReferenceLabel}:</span>
                <span className="shrink inline-block text-right ms-auto">
                  <button
                    type="button"
                    ref={tooltipTriggerRef}
                    onClick={() => {
                      if (!showTooltip && tooltipTriggerRef.current && phoneScreenRef.current) {
                        const btnRect = tooltipTriggerRef.current.getBoundingClientRect();
                        const phoneRect = phoneScreenRef.current.getBoundingClientRect();
                        setTooltipPos({
                          top: btnRect.bottom - phoneRect.top + 6,
                          left: btnRect.left - phoneRect.left + btnRect.width / 2 - 20,
                        });
                      }
                      setShowTooltip(v => !v);
                    }}
                    className="font-mono text-primary underline decoration-dashed focus:outline-none custom-line-break"
                  >
                    {customerReferenceNumber}
                  </button>
                </span>
              </div>
              {showTooltip && phoneScreenRef.current && createPortal(
                <>
                  <div className="absolute inset-0 z-[10]" onClick={() => setShowTooltip(false)} />
                  <div
                    data-theme={currentTheme}
                    style={{ position: CSS_POSITION_ABSOLUTE, top: tooltipPos.top, left: 12, right: 12, zIndex: 20, borderRadius: "10px" }}
                  >
                    {/* Arrow */}
                    <div className="border-b-primary" style={{ position: CSS_POSITION_ABSOLUTE, top: -9, left: 'auto', right: 0, transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottomWidth: 9, borderBottomStyle: 'solid' }} />
                    <div className="border-b-base-100" style={{ position: CSS_POSITION_ABSOLUTE, top: -6, left: 'auto', right: 0, transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottomWidth: 8, borderBottomStyle: 'solid' }} />
                    <div className="bg-base-100 border-2 custom-line-break border-primary rounded-[8px] p-3 shadow-lg w-full">
                      <div className="space-y-0.5 text-[9px] text-base-content leading-tight custom-line-break">
                        <p>Sent by: <span>{assigneeName ? assigneeName : ''}</span></p>
                        <p>Date: <span>{toLocal(new Date(), { month: 'long', day: 'numeric', year: 'numeric' })}</span></p>
                        <p>Reason: <span>{workflowName || ""}</span></p>
                      </div>
                    </div>
                  </div>
                </>,
                phoneScreenRef.current
              )}
            </div>
          </div>

          {/* Message Box */}
          <div className="px-3 mb-3">
            <div className="bg-primary/30 rounded-xl p-3.5 border border-primary/5 overflow-hidden">
              <p className="text-[10px] leading-relaxed text-base-content custom-line-break">
                {welcomeSubTitle}
              </p>
            </div>
          </div>

          {/* How it works section */}
          <div className="px-3 mb-3 flex-1 overflow-y-auto no-scrollbar">
            <h3 className="text-[16px] font-black text-base-content mb-4 tracking-tight">How it works</h3>

            <div className="space-y-3">
              <div className="flex gap-3.5 items-center">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <img src={isDark ? GoodLightingWhiteIcon : GoodLightingIcon} alt="" className="w-full h-full object-contain" />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-[13px] leading-tight text-base-content">Good lighting</span>
                  <span className="text-[10px] font-medium text-base-content leading-snug">Find a well-lit area. Natural daylight works best</span>
                </div>
              </div>

              <div className="flex gap-3.5 items-center">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <img src={isDark ? RecordMediaWhiteIcon : RecordMediaIcon} alt="" className="w-full h-full object-contain" />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-[13px] leading-tight text-base-content">Record media</span>
                  <span className="text-[10px] font-medium text-base-content leading-snug">Capture imagery from multiple angles</span>
                </div>
              </div>

              <div className="flex gap-3.5 items-center">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <img src={isDark ? SecureTransmissionWhiteIcon : SecureTransmissionIcon} alt="" className="w-full h-full object-contain" />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-[13px] leading-tight text-base-content">Secure transmission</span>
                  <span className="text-[10px] font-medium text-base-content leading-snug">Information is encrypted and shared securely</span>
                </div>
              </div>

              <div className="flex gap-3.5 items-center">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <img src={isDark ? ReviewSubmitWhiteIcon : ReviewSubmitIcon} alt="" className="w-full h-full object-contain" />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-[13px] leading-tight text-base-content">Review & submit</span>
                  <span className="text-[10px] font-medium text-base-content leading-snug">Quick guided capture process</span>
                </div>
              </div>
            </div>
          </div>

          <div className="px-3 pb-3 pt-2 bg-base-100 flex items-center gap-2 border-t border-base-200">
            {globalInstructionUrl && (
              <div
                title="Global instructions attached"
                className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-md border border-primary shrink-0"
              >
                <HelpCircle size={18} className="text-primary" />
              </div>
            )}
            <button className="btn btn-primary flex-1 h-[48px] text-primary-content font-bold text-[14px] rounded-xl shadow-lg border-none min-h-[48px]">
              Continue
            </button>
          </div>
        </div>
      );
    }

    if (current === 'instructions') {
      return (
        <div className="flex-1 flex flex-col font-sans bg-base-100 text-base-content min-h-full">
          {/* Header Row */}
          <div className="flex justify-between items-center px-3 pt-3 mb-3">
            <img src={logo} alt="TruVideo" className="w-auto max-w-[70px] h-[30px] max-h-[30px] object-contain" />
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-bold text-base-content">Expires:</span>
              <div className="bg-success/10 border border-success/20 rounded-full px-1 py-0.5 text-success font-bold text-[9px] tabular-nums">
                {formatted}
              </div>
            </div>
          </div>

          <div className="flex-1 px-3 pb-16 w-full flex flex-col">
            {/* Assignment Info */}
            <div className="mb-6 flex flex-col gap-0.5">
              <h1 className="text-[16px] font-black tracking-tight mb-0 text-base-content leading-none custom-line-break">
                {submitterName || 'John S.'}
              </h1>
              <div className="flex flex-wrap flex-col gap-0.5 text-[10px] text-base-content">
                <div className="flex flex-wrap justify-between items-start w-full">
                  <span className='custom-line-break'>{dynamicReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicReferenceLabel}:</span>
                  <span className="shrink max-w-full inline-block text-right break-all ms-auto">
                    <button
                      type="button"
                      ref={tooltipTriggerInstrRef}
                      onClick={() => {
                        if (!showTooltipInstr && tooltipTriggerInstrRef.current && phoneScreenRef.current) {
                          const btnRect = tooltipTriggerInstrRef.current.getBoundingClientRect();
                          const phoneRect = phoneScreenRef.current.getBoundingClientRect();
                          setTooltipPosInstr({
                            top: btnRect.bottom - phoneRect.top + 6,
                            left: btnRect.left - phoneRect.left + btnRect.width / 2 - 10,
                          });
                        }
                        setShowTooltipInstr(v => !v);
                      }}
                      className="font-mono text-primary underline decoration-dashed focus:outline-none custom-line-break"
                    >
                      {customerReferenceNumber}
                    </button>
                  </span>
                </div>
              </div>
            </div>
            {showTooltipInstr && phoneScreenRef.current && createPortal(
              <>
                <div className="absolute inset-0 z-[10]" onClick={() => setShowTooltipInstr(false)} />
                <div
                  data-theme={branding?.theme || 'light'}
                  style={{ position: CSS_POSITION_ABSOLUTE, top: tooltipPosInstr.top, left: 12, right: 12, zIndex: 20, borderRadius: "10px" }}
                >
                  <div className="border-b-primary" style={{ position: CSS_POSITION_ABSOLUTE, top: -9, left: 'auto', right: 0, transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottomWidth: 9, borderBottomStyle: 'solid' }} />
                  <div className="border-b-base-100" style={{ position: CSS_POSITION_ABSOLUTE, top: -6, left: 'auto', right: 0, transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottomWidth: 8, borderBottomStyle: 'solid' }} />
                  <div className="bg-base-100 border-2 border-primary rounded-[8px] p-3 shadow-lg w-full">
                    <div className="space-y-0.5 text-[9px] text-base-content leading-tight custom-line-break">
                      <p>Sent by: <span>{assigneeName ? assigneeName : ''}</span></p>
                      <p>Date: <span>{toLocal(new Date(), { month: 'long', day: 'numeric', year: 'numeric' })}</span></p>
                      <p>Reason: <span>{workflowName || ""}</span></p>
                    </div>
                  </div>
                </div>
              </>,
              phoneScreenRef.current
            )}

            {/* Section Title */}
            <div className="mb-4 custom-line-break">
              <h2 className="text-[16px] font-black tracking-tight mb-1.5 text-base-content">
                {branding?.instructionsMainTitle || "What you'll need to capture"}
              </h2>
              <p className="text-[11px] leading-snug text-base-content font-normal font-sans">
                {branding?.instructionsHeading || 'Follow each step. You\'ll be able to review all media before final submission.'}
              </p>
            </div>

            {/* Steps List */}
            <div className="space-y-4">
              {items.map((item, index) => {
                const isVideo = item.type === 'video' || item.description?.toLowerCase().includes('video');
                const isIdentifier = item.type === 'identifier';
                const isFile = item.type === 'file';
                let IconAsset;
                if (isIdentifier) {
                  IconAsset = isDark ? scannerWhiteIcon : scannerIcon;
                } else if (isVideo) {
                  IconAsset = isDark ? videoWhiteIcon : videoIcon;
                } else if (isFile) {
                  IconAsset = isDark ? filesWhiteIcon : filesIconAsset;
                } else {
                  IconAsset = isDark ? cameraWhiteIcon : cameraIcon;
                }

                return (
                  <div key={index} className="flex items-center gap-3 pl-2">
                    <div className="flex-shrink-0 pt-0 w-6 h-6 flex items-center justify-center">
                      <img src={IconAsset} alt="" className={`w-6 h-6 object-contain ${isFile ? 'p-[2px]' : ''}`} />
                    </div>
                    <div className="flex flex-col flex-1">
                      <p className="text-[12px] font-black leading-tight text-base-content mb-0.5">Step {index + 1}</p>
                      <p className="text-[11px] leading-snug text-base-content/80 custom-line-break">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="px-4 pb-4 w-full flex items-center justify-between absolute bottom-0 left-0 bg-base-100 z-10 pt-2">
            <button className="btn btn-primary font-bold text-[13px] rounded-[8px] shadow-sm transition-all border-none w-[70px] h-[34px] min-h-[34px] leading-none text-primary-content">
              Back
            </button>
            {globalInstructionUrl && (
              <div
                title="Global instructions attached"
                className="w-7 h-7 rounded-full bg-white flex items-center justify-center shadow-md border border-primary shrink-0"
              >
                <HelpCircle size={14} className="text-primary" />
              </div>
            )}
            <button className="btn btn-primary font-bold text-[13px] rounded-[8px] shadow-sm transition-all border-none w-[70px] h-[34px] min-h-[34px] leading-none text-primary-content">
              Start
            </button>
          </div>
        </div>
      );
    }

    if (current.startsWith('step-')) {
      const stepIndex = parseInt(current.replace('step-', ''), 10);
      const item = items[stepIndex] || { description: "Capture instruction", type: 'photo' };
      const totalSteps = items.length || DEFAULT_TOTAL_STEPS;

      return (
        <div className="flex-1 relative overflow-hidden bg-black min-h-full">
          {/* Background: camera feed or file upload placeholder */}
          {item.type === 'file' ? (
            <div className="absolute inset-0 bg-base-200 flex flex-col justify-end">
              <div className="w-full bg-base-100 text-base-content px-4 pt-5 pb-20 mt-3 flex flex-col z-[15] absolute top-[170px] bottom-0 shadow-[0px_-20px_20px_rgba(0,0,0,0.25)] rounded-t-[16px]">
                <p className="text-[14px] font-semibold text-base-content mb-3 pl-2">Location of file</p>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3 p-2 cursor-pointer rounded-[8px] text-base-content hover:bg-primary hover:text-white active:bg-primary active:text-white transition-all group">
                    <img src={filesIconAsset} alt="Files" className={`w-8 h-8 object-contain transition-all ${isDark ? 'invert' : 'group-hover:invert group-active:invert'}`} />
                    <span className="text-sm">Files</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="absolute inset-0 bg-[#000]">
              <img
                src={getImage(industry)}
                alt="Camera feed mock"
                className="absolute inset-0 w-full h-full object-cover opacity-[0.85]"
              />
              {/* Center subtle icon to imply it's a live feed preview */}
              {/* <div className="absolute inset-0 flex flex-col items-center justify-center opacity-30 mix-blend-overlay">
                {item.type === 'video' ? <Video size={64} className="text-white/40" strokeWidth={1} /> : <Camera size={64} className="text-white/40" strokeWidth={1} />}
              </div> */}
            </div>
          )}

          {/* Top Header Overlay Design */}
          <div className="absolute top-0 left-2 right-2 bg-black/60 p-3 pt-6 pb-2 border-b border-black/20 z-20 rounded-b-[1.25rem] shadow-lg xl-scale-down">
            {/* Row 1: Logo & Expiry */}
            <div className="flex justify-between items-center mb-2">
              <img src={branding?.logoUrl ? logo : logoWhite} alt="TruVideo" className="w-auto max-w-[70px] h-[30px] max-h-[30px] object-contain" />
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-medium text-white drop-shadow-md">Expires:</span>
                <div className="rounded-full py-0.5 px-1 font-bold text-[9px] shadow-sm leading-none bg-[#FFFBE4] border border-[#A65200] text-[#A65200]">
                  {formatted}
                </div>
              </div>
            </div>

            {/* Row 2: Submitter Identity */}
            <div className={`flex flex-col mb-2 gap-0.5 ${canExpand && !isExpanded ? 'hidden' : ''}`}>
              <div className="min-w-0">
                <h1
                  ref={nameTooltipTriggerStepRef}
                  className="text-white text-[15px] font-bold leading-none drop-shadow-md truncate cursor-pointer transition-opacity active:opacity-70"
                  onClick={() => {
                    if (!showNameTooltipStep && nameTooltipTriggerStepRef.current && phoneScreenRef.current) {
                      const btnRect = nameTooltipTriggerStepRef.current.getBoundingClientRect();
                      const phoneRect = phoneScreenRef.current.getBoundingClientRect();
                      setNameTooltipPosStep({
                        top: btnRect.bottom - phoneRect.top + 6,
                        left: btnRect.left - phoneRect.left + btnRect.width / 2 - 10,
                      });
                    }
                    setShowNameTooltipStep(v => !v);
                  }}
                >
                  {submitterName || 'John S.'}
                </h1>
                {showNameTooltipStep && phoneScreenRef.current && createPortal(
                  <>
                    <div className="absolute inset-0 z-[10]" onClick={() => setShowNameTooltipStep(false)} />
                    <div
                      data-theme={currentTheme}
                      style={{ position: CSS_POSITION_ABSOLUTE, top: nameTooltipPosStep.top, left: 12, right: 12, zIndex: 20, borderRadius: "10px" }}
                    >
                      <div className="border-b-primary" style={{ position: CSS_POSITION_ABSOLUTE, top: -9, left: 20, right: 'auto', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottomWidth: 9, borderBottomStyle: 'solid' }} />
                      <div className="border-b-base-100" style={{ position: CSS_POSITION_ABSOLUTE, top: -6, left: 20, right: 'auto', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottomWidth: 8, borderBottomStyle: 'solid' }} />
                      <div className="bg-base-100 border-2 border-primary rounded-[8px] p-3 shadow-lg w-full">
                        <div className="text-[10px] text-base-content font-bold leading-tight custom-line-break">
                          {submitterName || 'John S.'}
                        </div>
                      </div>
                    </div>
                  </>,
                  phoneScreenRef.current
                )}
              </div>
              <div className="text-[10px] text-white/90 drop-shadow-md">
                <div className="flex flex-wrap justify-between items-start w-full custom-line-break">
                  <span>{dynamicReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicReferenceLabel}:</span>
                  <span className="shrink inline-block text-right">
                    <button
                      type="button"
                      ref={tooltipTriggerStepRef}
                      onClick={() => {
                        if (!showTooltipStep && tooltipTriggerStepRef.current && phoneScreenRef.current) {
                          const btnRect = tooltipTriggerStepRef.current.getBoundingClientRect();
                          const phoneRect = phoneScreenRef.current.getBoundingClientRect();
                          setTooltipPosStep({
                            top: btnRect.bottom - phoneRect.top + 6,
                            left: btnRect.left - phoneRect.left + btnRect.width / 2 - 10,
                          });
                        }
                        setShowTooltipStep(v => !v);
                      }}
                      className="font-mono text-primary underline decoration-dashed focus:outline-none custom-line-break"
                    >
                      {customerReferenceNumber}
                    </button>
                  </span>
                </div>
              </div>
            </div>
            {showTooltipStep && phoneScreenRef.current && createPortal(
              <>
                <div className="absolute inset-0 z-[10]" onClick={() => setShowTooltipStep(false)} />
                <div
                  data-theme={currentTheme}
                  style={{ position: CSS_POSITION_ABSOLUTE, top: tooltipPosStep.top, left: 12, right: 12, zIndex: 20, borderRadius: "10px" }}
                >
                  <div className="border-b-primary" style={{ position: CSS_POSITION_ABSOLUTE, top: -9, left: 'auto', right: 0, transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottomWidth: 9, borderBottomStyle: 'solid' }} />
                  <div className="border-b-base-100" style={{ position: CSS_POSITION_ABSOLUTE, top: -6, left: 'auto', right: 0, transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottomWidth: 8, borderBottomStyle: 'solid' }} />
                  <div className="bg-base-100 custom-line-break border-2 border-primary rounded-[8px] p-3 shadow-lg w-full">
                    <div className="space-y-0.5 text-[9px] text-base-content leading-tight custom-line-break">
                      <p>Sent by: <span>{assigneeName ? assigneeName : ''}</span></p>
                      <p>Date: <span>{toLocal(new Date(), { month: 'long', day: 'numeric', year: 'numeric' })}</span></p>
                      <p>Reason: <span>{workflowName || ""}</span></p>
                    </div>
                  </div>
                </div>
              </>,
              phoneScreenRef.current
            )}

            {/* Row 3: Progress Bar */}
            <div className="flex items-center gap-1.5 mb-2 w-full">
              <span className="text-[10px] font-medium text-white whitespace-nowrap drop-shadow-md">Step {stepIndex + 1} of {totalSteps}</span>
              <div className="flex-1 flex h-[4px] gap-[2px]">
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <div
                    key={i}
                    className={`flex-1 rounded-[1.5px] transition-all duration-500 ${i < stepIndex + 1 ? 'bg-primary' : 'bg-white/40'}`}
                  />
                ))}
              </div>
            </div>

            {/* Row 4: Instruction & Required Badge */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0 max-w-[75%]">
                {item.referenceMediaUrl && (
                  <div
                    title="Reference media attached"
                    className="w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow-sm border border-primary shrink-0"
                  >
                    <HelpCircle size={11} className="text-white" />
                  </div>
                )}
                <p
                  ref={instructionRef}
                  className={`text-white text-[15px] break-words font-bold tracking-tight drop-shadow-md leading-snug min-w-0 flex-1 ${!isExpanded ? 'truncate' : ''}`}
                >
                  {item.description}
                </p>
              </div>
              {item.required && (
                <span className="bg-[#F7C1C1] text-[#990000] border border-[#990000] font-extrabold text-[8px] px-1.5 py-0.5 rounded-full uppercase shadow-sm shrink-0">
                  REQUIRED
                </span>
              )}
            </div>

            {/* Expand/Collapse Arrow - Only show if text is truncated */}
            {canExpand && (
              <div className="flex justify-center mt-1 -mb-1">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-white/80 hover:text-white transition-colors focus:outline-none p-1.5"
                >
                  {isExpanded ? (
                    <svg width="28" height="9" viewBox="0 0 28 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.5 7.5L14 1.5L26.5 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg width="28" height="9" viewBox="0 0 28 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.5 1.5L14 7.5L26.5 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </div>
            )}
          </div>


          {/* Bottom Buttons */}
          {item.type === 'file' ? (
            <div style={{ position: CSS_POSITION_ABSOLUTE, bottom: 20, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: '12px', paddingRight: '12px', zIndex: 20 }}>
              <button className="w-[60px] h-[28px] rounded-[8px] bg-primary text-white font-bold text-[11px] border-none shadow-md">
                Back
              </button>

              {globalInstructionUrl && (
                <div
                  title="Global instructions attached"
                  className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-md border border-primary shrink-0"
                >
                  <HelpCircle size={16} className="text-primary" />
                </div>
              )}

              {!item.required ? (
                <button className="w-[60px] h-[28px] rounded-[8px] bg-primary text-white font-bold text-[11px] border-none shadow-md">
                  Skip
                </button>
              ) : (
                <div className="w-[60px]" />
              )}
            </div>
          ) : (
            <div style={{ position: CSS_POSITION_ABSOLUTE, bottom: 20, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: '12px', paddingRight: '12px', zIndex: 20 }}>
              <div className="w-[60px] flex justify-end">
                {globalInstructionUrl && (
                  <div
                    title="Global instructions attached"
                    className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-md border border-primary"
                  >
                    <HelpCircle size={16} className="text-primary" />
                  </div>
                )}
              </div>

              {/* Shutter Button */}
              <div style={{ position: 'relative' }}>
                <div className="absolute -inset-[3.5px] rounded-full border-[2.5px] border-white shadow-lg pointer-events-none" />

                {item.type === 'photo' && (
                  <button className="w-[50px] h-[50px] rounded-full bg-primary flex items-center justify-center shadow-md border-none relative z-10 outline-none">
                    <img src={cameraWhiteIcon} alt="Capture" className="w-[28px] object-contain pointer-events-none" />
                  </button>
                )}

                {item.type === 'video' && (
                  <button className="w-[50px] h-[50px] rounded-full bg-primary flex items-center justify-center shadow-md border-none relative z-10 outline-none">
                    <img src={videoWhiteIcon} alt="Record" className="w-[28px] object-contain pointer-events-none" />
                  </button>
                )}

                {item.type === 'identifier' && (
                  <button className="w-[50px] h-[50px] rounded-full bg-primary flex items-center justify-center shadow-md border-none relative z-10 text-white outline-none">
                    <img src={scannerWhiteIcon} alt="scanner-white" className="w-[28px] object-contain pointer-events-none" />
                  </button>
                )}
              </div>

              {/* Skip Button - Only show if step is not required */}
              {!item.required ? (
                <button className="w-[60px] h-[28px] rounded-[8px] bg-primary text-white font-bold text-[11px] border-none shadow-md">
                  Skip
                </button>
              ) : (
                <div className="w-[60px]" />
              )}
            </div>
          )}
        </div>
      );
    }

    if (current === 'done') {
      const doneTitle = branding?.thankyouTitle || 'Media submitted!';
      const doneTitleWithName = doneTitle.replaceAll('{name}', submitterName || '{name}');
      const whatHappensNextTitle = branding?.whatHappensNextTitle || 'What happens next';
      const whatHappensNextDescription = branding?.whatHappensNextDescription || 'Your submitted imagery will be reviewed. You will receive updates once the process is complete.';
      const photosCount = items.filter(i => i.type === 'photo' || i.type === 'identifier').length || 4;
      const filesCount = items.filter(i => i.type === 'file').length || 1;
      const videosCount = items.filter(i => i.type === 'video').length || 1;

      return (
        <div className="flex-1 flex flex-col font-sans bg-base-100 text-base-content min-h-full">
          {/* Header Row */}
          <div className="flex justify-between items-center px-3 pt-3 mb-3">
            <img src={logo} alt="TruVideo" className="w-auto max-w-[70px] h-[30px] max-h-[30px] object-contain" />
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-bold text-base-content">Expires:</span>
              <div className="bg-success/10 border border-success/20 rounded-full px-1 py-0.5 text-success font-bold text-[9px] tabular-nums">
                {formatted}
              </div>
            </div>
          </div>

          {/* User Info Row */}
          <div className="px-4 mb-6 flex flex-col gap-0.5">
            <div className="min-w-0">
              <h1
                ref={nameTooltipTriggerDoneRef}
                className="text-[14px] font-black tracking-tight text-base-content leading-tight truncate cursor-pointer transition-opacity active:opacity-70"
                onClick={() => {
                  if (!showNameTooltipDone && nameTooltipTriggerDoneRef.current && phoneScreenRef.current) {
                    const btnRect = nameTooltipTriggerDoneRef.current.getBoundingClientRect();
                    const phoneRect = phoneScreenRef.current.getBoundingClientRect();
                    setNameTooltipPosDone({
                      top: btnRect.bottom - phoneRect.top + 6,
                      left: btnRect.left - phoneRect.left + btnRect.width / 2 - 10,
                    });
                  }
                  setShowNameTooltipDone(v => !v);
                }}
              >
                {submitterName || 'John S.'}
              </h1>
              {showNameTooltipDone && phoneScreenRef.current && createPortal(
                <>
                  <div className="absolute inset-0 z-[10]" onClick={() => setShowNameTooltipDone(false)} />
                  <div
                    data-theme={currentTheme}
                    style={{ position: CSS_POSITION_ABSOLUTE, top: nameTooltipPosDone.top, left: 12, right: 12, zIndex: 20 }}
                  >
                    <div className="border-b-primary" style={{ position: CSS_POSITION_ABSOLUTE, top: -9, left: 20, right: 'auto', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottomWidth: 9, borderBottomStyle: 'solid' }} />
                    <div className="border-b-base-100" style={{ position: CSS_POSITION_ABSOLUTE, top: -6, left: 20, right: 'auto', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottomWidth: 8, borderBottomStyle: 'solid' }} />
                    <div className="bg-base-100 border-2 border-primary rounded-[8px] p-3 shadow-lg w-full">
                      <div className="text-[10px] text-base-content font-bold leading-tight custom-line-break">
                        {submitterName || 'John S.'}
                      </div>
                    </div>
                  </div>
                </>,
                phoneScreenRef.current
              )}
            </div>
            <div className="text-[10px] text-base-content">
              <div className="flex flex-wrap justify-between items-start w-full custom-line-break">
                <span className='custom-line-break'>{dynamicReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicReferenceLabel}: </span>
                <span className="shrink inline-block text-right">
                  <button
                    type="button"
                    ref={tooltipTriggerDoneRef}
                    onClick={() => {
                      if (!showTooltipDone && tooltipTriggerDoneRef.current && phoneScreenRef.current) {
                        const btnRect = tooltipTriggerDoneRef.current.getBoundingClientRect();
                        const phoneRect = phoneScreenRef.current.getBoundingClientRect();
                        setTooltipPosDone({
                          top: btnRect.bottom - phoneRect.top + 6,
                          left: btnRect.left - phoneRect.left + btnRect.width / 2 - 10,
                        });
                      }
                      setShowTooltipDone(v => !v);
                    }}
                    className="font-mono text-primary underline decoration-dashed focus:outline-none custom-line-break"
                  >
                    {customerReferenceNumber}
                  </button>
                </span>
              </div>
            </div>
          </div>
          {showTooltipDone && phoneScreenRef.current && createPortal(
            <>
              <div className="absolute inset-0 z-[10]" onClick={() => setShowTooltipDone(false)} />
              <div
                data-theme={currentTheme}
                style={{ position: CSS_POSITION_ABSOLUTE, top: tooltipPosDone.top, left: 12, right: 12, zIndex: 20 }}
              >
                <div className="border-b-primary" style={{ position: CSS_POSITION_ABSOLUTE, top: -9, left: 'auto', right: 0, transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottomWidth: 9, borderBottomStyle: 'solid' }} />
                <div className="border-b-base-100" style={{ position: CSS_POSITION_ABSOLUTE, top: -6, left: 'auto', right: 0, transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottomWidth: 8, borderBottomStyle: 'solid' }} />
                <div className="bg-base-100 border-2 border-primary rounded-[8px] p-3 shadow-lg w-full">
                  <div className="space-y-0.5 text-[9px] text-base-content leading-tight custom-line-break">
                    <p>Sent by: <span>{assigneeName ? assigneeName : ''}</span></p>
                    <p>Date: <span>{toLocal(new Date(), { month: 'long', day: 'numeric', year: 'numeric' })}</span></p>
                    <p>Reason: <span>{workflowName || ""}</span></p>
                  </div>
                </div>
              </div>
            </>,
            phoneScreenRef.current
          )}

          {/* Success Status */}
          <div className="flex-1 px-4 flex flex-col items-center">


            <h2 className="text-[16px] font-black mb-0 custom-line-break tracking-tight text-base-content text-start w-full">{doneTitleWithName}</h2>
            <p className="text-[10px] custom-line-break font-medium text-base-content text-start mb-1 w-full">
              {branding?.thankyouMessage || 'Your items have been securely sent.'}
            </p>
            <div className="w-full mb-6 mt-4 flex gap-2 px-0">
              <div className="flex-1 bg-[#D9D9D9] rounded-[8px] py-3 px-1.5 flex flex-col items-center justify-center">
                <span className="text-[11px] font-normal text-black">Images</span>
                <span className="text-[28px] font-normal text-black mt-[3.5px] leading-none">{photosCount}</span>
              </div>
              <div className="flex-1 bg-[#D9D9D9] rounded-[8px] py-3 px-1.5 flex flex-col items-center justify-center">
                <span className="text-[11px] font-normal text-black">Files</span>
                <span className="text-[28px] font-normal text-black mt-[3.5px] leading-none">{filesCount}</span>
              </div>
              <div className="flex-1 bg-[#D9D9D9] rounded-[8px] py-3 px-1.5 flex flex-col items-center justify-center">
                <span className="text-[11px] font-normal text-black">Videos</span>
                <span className="text-[28px] font-normal text-black mt-[3.5px] leading-none">{videosCount}</span>
              </div>
            </div>

            {/* Next Steps */}
            <div className="w-full mb-8">
              <h3 className="text-[12px] font-black text-base-content mb-3 tracking-tight">{whatHappensNextTitle}</h3>
              <div className="bg-primary/30 rounded-xl p-4 border border-primary/5">
                <p className="text-[10px] custom-line-break font-medium leading-relaxed text-base-content">
                  {whatHappensNextDescription}
                </p>
              </div>
            </div>
            <div className="pt-5">
              <h2 className="text-[12px] font-black text-base-content tracking-tight">Safe to close this window</h2>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-[280px]">
        {/* Modern Device Frame Wrapper (Polished Scale to prevent overflow) */}
        <div className="bg-black rounded-[3rem] p-1.5 dark:ring-white/10 ring-1 ring-black/5 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.4)]">
          <div className="bg-[#111] rounded-[2.75rem] p-2 overflow-hidden relative">

            {/* Themed Mobile Screen Content */}
            <div ref={phoneScreenRef} data-theme={branding?.theme || 'light'} className="phone-preview-screen bg-base-100 rounded-[2rem] overflow-hidden flex flex-col h-[580px] relative" style={{ fontFamily: "'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
              {/* Dynamic Content */}
              <div className="flex-1 overflow-y-auto no-scrollbar scroll-smooth">
                {renderScreen()}
              </div>
            </div>

            {/* Fake Hardware Elements */}
            <div className="absolute top-0 left-0 right-0 h-6 flex justify-center pointer-events-none">
              <div className="h-4 w-24 bg-[#111] rounded-b-xl border-x border-b border-white/5" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhonePreview;

PhonePreview.propTypes = {
  workflowName: PropTypes.string,
  name: PropTypes.string,
  submitterName: PropTypes.string,
  assigneeName: PropTypes.string,
  customerReferenceNumber: PropTypes.string,
  customerReferenceLabel: PropTypes.string,
  systemReferenceNumber: PropTypes.string,
  industry: PropTypes.string,
  description: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      type: PropTypes.string,
      required: PropTypes.bool,
      referenceMediaUrl: PropTypes.string,
    })
  ),
  previewStep: PropTypes.number,
  branding: PropTypes.shape({
    theme: PropTypes.string,
    logoUrl: PropTypes.string,
    welcomeSubtitle: PropTypes.string,
    instructionsMainTitle: PropTypes.string,
    instructionsHeading: PropTypes.string,
    thankyouTitle: PropTypes.string,
    whatHappensNextTitle: PropTypes.string,
    whatHappensNextDescription: PropTypes.string,
    thankyouMessage: PropTypes.string,
  }),
  expiresAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  workflowIndustryLogoUrls: {
    general: PropTypes.string,
    aviation: PropTypes.string,
    insurance: PropTypes.string,
    automotive: PropTypes.string,
    fieldServices: PropTypes.string,
  },
  globalInstructionUrl: PropTypes.string,
};
